import React, { useMemo } from 'react'
import { isDefined } from '@towns-protocol/sdk'
import { Box, BoxProps, Paragraph } from '@ui'
import { useNfts } from 'hooks/useNfts'
import { pillShimmerClass } from 'ui/styles/globals/shimmer.css'
import {
    EVERYONE_ADDRESS,
    Permission,
    RoleDetails,
    useContractSpaceInfo,
    useLinkedAndDelegatedWallets,
    useMultipleRoleDetails,
    useRoles,
    useSpaceId,
} from 'use-towns-client'

type Props = {
    userId: string | undefined
}

export const UserRoles = (props: Props) => {
    const { userId } = props
    const spaceId = useSpaceId()

    if (!userId || !spaceId) {
        return null
    }

    return (
        <Box gap padding background="level2" borderRadius="sm">
            <Paragraph fontWeight="strong" color="default">
                Roles
            </Paragraph>
            <UserRolePills userId={userId} spaceId={spaceId} />
        </Box>
    )
}

const MODERATOR_PERMISSIONS: Permission[] = [
    Permission.Redact,
    Permission.PinMessage,
    Permission.ModifyBanning,
    Permission.AddRemoveChannels,
    Permission.ModifySpaceSettings,
]

const MINTER_ROLE = 1

const DEFAULT_ROLE_NAMES: { [key: string]: string } = {
    2: 'Member',
}

export const UserRolePills = (props: { userId: string; spaceId: string }) => {
    const { userId, spaceId } = props

    const { spaceRoles, isLoading: isLoadingRoles } = useRoles(spaceId)
    const roleIds = useMemo(() => spaceRoles?.map((role) => role.roleId) || [], [spaceRoles])

    const { data: linkedWallets } = useLinkedAndDelegatedWallets({ walletAddress: userId })

    const { data: roleDetails, isLoading: isLoadingRoleDetails } = useMultipleRoleDetails(
        spaceId,
        roleIds,
    )

    const hasRuleData = roleDetails?.some((r) => r.ruleData.rules.checkOperations.length > 0)
    const { nfts } = useNfts({ walletAddress: userId, enabled: !!hasRuleData })

    const matchingRoles = useMemo(() => {
        return linkedWallets
            ? roleDetails
                  ?.filter(
                      (r) =>
                          (r.id !== MINTER_ROLE && r.users.includes(EVERYONE_ADDRESS)) ||
                          linkedWallets?.some((w) => r.users.includes(w)) ||
                          nfts.some((nft) =>
                              r.ruleData.rules.checkOperations.some(
                                  (op) =>
                                      (op.contractAddress as string).toLowerCase() ===
                                      nft.data.address.toLowerCase(),
                              ),
                          ),
                  )
                  .filter(isDefined)
            : []
    }, [linkedWallets, roleDetails, nfts])

    const { data: spaceData } = useContractSpaceInfo(spaceId)
    const owner = spaceData?.owner

    const isOwner = owner && linkedWallets?.some((w) => w.toLowerCase() === owner.toLowerCase())

    if (isLoadingRoles || isLoadingRoleDetails) {
        return <LoadingPill />
    }

    if (!matchingRoles || matchingRoles.length === 0) {
        return <NoRolesPill />
    }

    return (
        <Box horizontal flexWrap="wrap" display="flex" gap="xs">
            {isOwner && (
                <RolePill
                    isAdmin
                    roleDetails={{
                        id: 0,
                        name: 'Founder',
                        permissions: [],
                    }}
                    spaceId={spaceId}
                    userId={userId}
                />
            )}
            {matchingRoles.map((r) => (
                <RolePill key={r.id} roleDetails={r} spaceId={spaceId} userId={userId} />
            ))}
        </Box>
    )
}

const RolePill = (
    props: {
        roleDetails: Pick<RoleDetails, 'id' | 'permissions' | 'name'>
        userId: string
        spaceId: string
        type?: 'member' | 'admin' | 'founder'
        isAdmin?: boolean
    } & BoxProps,
) => {
    const { roleDetails, isAdmin } = props

    const boxProps = useMemo((): Partial<BoxProps> => {
        if (roleDetails.permissions.some((p) => MODERATOR_PERMISSIONS.includes(p))) {
            return { background: 'cta2', color: 'default' }
        }
        return { background: 'level3', color: 'default' }
    }, [roleDetails.permissions])

    return (
        <RolePillLayout {...boxProps} isAdmin={isAdmin}>
            {DEFAULT_ROLE_NAMES[roleDetails.id] ?? roleDetails.name}
        </RolePillLayout>
    )
}

const LoadingPill = () => {
    return (
        <RolePillLayout color="gray2" className={pillShimmerClass}>
            Loading Roles
        </RolePillLayout>
    )
}

const NoRolesPill = () => {
    return (
        <RolePillLayout color="gray2" background="level2">
            No Roles
        </RolePillLayout>
    )
}

const RolePillLayout = (
    props: {
        isAdmin?: boolean
        children: string
    } & BoxProps,
) => {
    const { children, isAdmin, ...boxProps } = props
    return (
        <Box
            centerContent
            paddingX="sm"
            height="x4"
            borderRadius="sm"
            width="fit-content"
            {...boxProps}
        >
            <Paragraph truncate size="sm" color={isAdmin ? 'rainbow' : undefined}>
                {children}
            </Paragraph>
        </Box>
    )
}
